/**
 * Query shape — the value-free identity of a database request.
 *
 * Everything the detector reasons about is derived from this shape: two queries
 * that share a signature are two requests a batched read could collapse into
 * one. Filter *values* are never part of a shape, so signatures and reports
 * cannot leak customer data.
 */

export type QueryOperation = "delete" | "insert" | "rpc" | "select" | "update";

export interface ObservedQuery {
  /**
   * Whether the request matched a whole set in one round trip (`in()`, `or()`).
   * A set filter is the shape the detector would otherwise recommend, so
   * `.in("id", ids)` and a per-row `.eq("id", id)` must never collapse into one
   * signature — otherwise every batched read looks like the N+1 it prevents.
   */
  readonly batched?: boolean;
  /**
   * Wall-clock duration of the round trip. Queries are recorded when they are
   * issued, so this is filled in once the request settles.
   */
  durationMs?: number;
  /**
   * Filter columns in call order for table queries, or argument names for
   * `rpc`. Column names come from code, never from user input or row values.
   */
  readonly filterColumns: readonly string[];
  readonly operation: QueryOperation;
  /**
   * Whether the read was paged (`range()`). Paged reads repeat by design, so
   * they carry a distinct signature and are excluded from burst analysis; a
   * per-row lookup never pages, so nothing real is hidden.
   */
  readonly paginated?: boolean;
  /** Monotonic-ish timestamp (ms) taken when the query was issued. */
  readonly startedAt: number;
  /** Table name, or the function name for `rpc`. */
  readonly table: string;
}

/** The parts of a query that define its shape; timing is irrelevant here. */
export type QueryShape = Pick<
  ObservedQuery,
  "batched" | "filterColumns" | "operation" | "paginated" | "table"
>;

/** Traits that make a repeated shape expected rather than suspicious. */
function describeTraits(shape: QueryShape): string {
  const traits: string[] = [];
  if (shape.batched) {
    traits.push("+batched");
  }
  if (shape.paginated) {
    traits.push("+paged");
  }
  return traits.length === 0 ? "" : ` ${traits.join(" ")}`;
}

/**
 * Stable, value-free identity of a query shape. Two queries share a signature
 * when they hit the same table with the same operation, the same filter
 * columns, and the same batching/paging traits.
 */
export function querySignature(shape: QueryShape): string {
  const traits = describeTraits(shape);
  return `${shape.operation} ${shape.table}(${shape.filterColumns.join(",")})${traits}`;
}

/**
 * Whether repeats of this shape are expected. A set filter already collapses
 * many rows into one request and a paged read repeats the same shape per page,
 * so counting either as an N+1 would flag the very code that fixes N+1s. A
 * per-row lookup does neither, so nothing real is hidden.
 */
export function repeatsByDesign(shape: QueryShape): boolean {
  return shape.batched === true || shape.paginated === true;
}

/** A query or report a suppression pattern can be matched against. */
export interface SuppressionTarget {
  readonly signature: string;
  readonly table: string;
}

/**
 * Whether an explicit allowlist covers this query. A pattern is either a full
 * signature (`select gl_invoices(id)`, exact — traits included) or a bare table
 * or rpc name (`gl_invoices`), which covers every shape on it.
 */
export function matchesSuppression(
  patterns: readonly string[],
  target: SuppressionTarget,
): boolean {
  return patterns.some((pattern) => pattern === target.signature || pattern === target.table);
}

function describeColumns(shape: QueryShape): string {
  if (shape.filterColumns.length === 0) {
    return "no filter";
  }
  return shape.filterColumns.join(", ");
}

/** Batching hint tailored to the repeated query shape. */
export function buildRemediation(shape: QueryShape): string {
  if (shape.operation === "rpc") {
    return `Call ${shape.table} once with an array argument instead of once per row.`;
  }
  if (shape.operation !== "select") {
    return `Batch the ${shape.table} ${shape.operation} into one request with an array payload or a single filter covering every row.`;
  }
  if (shape.filterColumns.length === 1) {
    const [column] = shape.filterColumns;
    return `Collapse into one read: .in("${column}", ids), or embed ${shape.table} in the parent select.`;
  }
  return `Collapse into one read over ${describeColumns(shape)} (single .in()/.or() filter or an embedded select) instead of one request per row.`;
}
